import { useEffect, useState } from "react";
import { Link, NavLink } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { Menu, X, ArrowUpRight } from "lucide-react";
import { NAV_LINKS, SOLUTIONS } from "@/data/content";
import { Logo } from "@/components/shared/Logo";
import { SolutionsMenu } from "./SolutionsMenu";

export const Navbar = () => {
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 24);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  return (
    <header
      data-testid="site-header"
      className={`fixed inset-x-0 top-0 z-50 border-b transition-[background-color,border-color] duration-300 ${
        scrolled || open ? "border-line bg-navy/85 backdrop-blur-xl" : "border-transparent bg-navy/40 backdrop-blur-md"
      }`}
    >
      <div className="wrap flex h-[76px] items-center justify-between">
        <Link to="/" data-testid="nav-logo-link" aria-label="Wexoz home" className="flex items-center">
          <Logo variant="wordmark" className="h-8 w-auto" />
        </Link>

        <nav className="hidden items-center gap-1 lg:flex" aria-label="Primary">
          {NAV_LINKS.map((l) =>
            l.path === "/solutions" ? (
              <SolutionsMenu key={l.path} linkClass={({ isActive }) => `relative rounded-full px-4 py-2 text-sm font-medium transition-colors duration-200 hover:text-white ${isActive ? "text-white" : "text-slate-400"}`} />
            ) : (
            <NavLink
              key={l.path}
              to={l.path}
              end={l.path === "/"}
              data-testid={`nav-link-${l.name.toLowerCase().replace(/\s+/g, "-")}`}
              className={({ isActive }) =>
                `relative rounded-full px-4 py-2 text-sm font-medium transition-colors duration-200 hover:text-white ${
                  isActive ? "text-white" : "text-slate-400"
                }`
              }
            >
              {({ isActive }) => (
                <>
                  {l.name}
                  {isActive && (
                    <motion.span layoutId="nav-dot" className="absolute -bottom-0.5 left-1/2 h-1 w-1 -translate-x-1/2 rounded-full bg-cyan-400" />
                  )}
                </>
              )}
            </NavLink>
            )
          )}
        </nav>

        <div className="flex items-center gap-3">
          <Link to="/contact" data-testid="nav-talk-to-expert-cta" className="btn-primary hidden !py-2.5 sm:inline-flex">
            Talk to an Expert <ArrowUpRight className="h-4 w-4" />
          </Link>
          <button
            data-testid="nav-mobile-toggle"
            aria-label="Toggle menu"
            aria-expanded={open}
            onClick={() => setOpen((v) => !v)}
            className="inline-flex h-10 w-10 items-center justify-center rounded-full border border-line text-white lg:hidden"
          >
            {open ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
          </button>
        </div>
      </div>

      <AnimatePresence>
        {open && (
          <motion.nav
            data-testid="nav-mobile-menu"
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: "auto", opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.3, ease: [0.22, 1, 0.36, 1] }}
            className="overflow-hidden border-t border-line bg-navy lg:hidden"
          >
            <div className="wrap flex flex-col py-4">
              {NAV_LINKS.map((l, i) => (
                <motion.div key={l.path} initial={{ x: -12, opacity: 0 }} animate={{ x: 0, opacity: 1 }} transition={{ delay: 0.04 * i }}>
                  <NavLink
                    to={l.path}
                    onClick={() => setOpen(false)}
                    data-testid={`mobile-nav-link-${l.name.toLowerCase().replace(/\s+/g, "-")}`}
                    className="block border-b border-line/60 py-4 font-heading text-lg text-white"
                  >
                    {l.name}
                  </NavLink>
                  {l.path === "/solutions" && (
                    <div className="grid grid-cols-2 gap-x-4 border-b border-line/60 py-3">
                      {SOLUTIONS.map((s) => (
                        <Link key={s.slug} to={`/solutions/${s.slug}`} onClick={() => setOpen(false)} data-testid={`mobile-nav-solution-${s.slug}`} className="py-1.5 text-sm text-slate-400 hover:text-cyan-400">{s.title}</Link>
                      ))}
                    </div>
                  )}
                </motion.div>
              ))}
              <Link to="/contact" onClick={() => setOpen(false)} data-testid="mobile-nav-cta" className="btn-primary mt-5 justify-center">
                Talk to an Expert
              </Link>
            </div>
          </motion.nav>
        )}
      </AnimatePresence>
    </header>
  );
};
